import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ConfirmModal from "../../common/ConfirmModal";
import ReportTable from "../../common/ReportTable/ReportTable";
import DateRangeFilter from "../../common/DateRangeFilter";
import IsfPageTour from "../isf/IsfPageTour";
import "../isf/IsfPageTour.scss";
import utils from "../../../utils/utils";
import QmsModalPortal from "./QmsModalPortal";
import QmsQuestionEntrySheet from "./QmsQuestionEntrySheet";
import QmsQuestionImportSheet from "./QmsQuestionImportSheet";
import {
  fetchQuestionsV2,
  fetchQuestionV2,
  saveQuestionV2,
  saveQuestionsBulkV2,
  deleteQuestionV2,
} from "./qmsService";
import {
  firstSuccessStoryRow,
  getApiTable,
  isApiSuccess,
  apiMessage,
} from "./qmsApiUtils";
import {
  DEFAULT_QUESTION_OPTIONS,
  buildQuestionOptionsXml,
  buildQuestionsBulkXml,
  createEmptyQuestionDraft,
  validateQuestionDraft,
} from "./qmsQuizQuestionUtils";
import {
  downloadQuestionImportTemplate,
  parseQuestionImportFile,
  validateImportDrafts,
  setCorrectOptionNumber,
} from "./qmsQuestionImportUtils";
import { buildQmsQuestionBankListTourSteps } from "./qmsQuestionBankListTourSteps";
import { buildQmsQuestionFormTourSteps } from "./qmsQuestionFormTourSteps";
import {
  hasCompletedQmsQuestionBankListTour,
  hasCompletedQmsQuestionFormTour,
  markQmsQuestionBankListTourCompleted,
  markQmsQuestionFormTourCompleted,
} from "./qmsQuizManagerTourStorage";
import { useQmsAccess } from "./qmsPermissionUtils";
import {
  QMS_COLUMN_DATE_PRESETS,
  QMS_EMPTY_DATE_RANGE,
  matchesQmsDateRange,
} from "./qmsColumnDateFilterUtils";

const MODAL_FORM = "form";
const MODAL_IMPORT = "import";

const QUESTION_FIELD_OPTIONS = [
  { key: "QuestionText", label: "Question" },
  { key: "OptionCount", label: "Options" },
  { key: "QuizUsageCount", label: "Used in quizzes" },
  { key: "CreatedAt", label: "Created" },
];

const DEFAULT_QUESTION_COLUMNS = [
  "QuestionText",
  "OptionCount",
  "QuizUsageCount",
  "CreatedAt",
];

const QmsQuestionBankPanel = ({
  userData,
  showLoader,
  hideLoader,
  showAlert,
  onQuestionsChanged,
  actionsSlot = null,
}) => {
  const {
    canQmsQuestionAddEdit,
    canQmsQuestionDelete,
    canQmsQuestionImportBulk,
  } = useQmsAccess();
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [createdDateRange, setCreatedDateRange] = useState(QMS_EMPTY_DATE_RANGE);
  const [modalMode, setModalMode] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [formDrafts, setFormDrafts] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [importDrafts, setImportDrafts] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importRowErrors, setImportRowErrors] = useState({});
  const [importFileName, setImportFileName] = useState("");
  const [confirmModal, setConfirmModal] = useState({ open: false, row: null });
  const [showListTour, setShowListTour] = useState(false);
  const [showFormTour, setShowFormTour] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const importInputRef = useRef(null);

  const questionListTourSteps = useMemo(() => buildQmsQuestionBankListTourSteps(), []);
  const questionFormTourSteps = useMemo(
    () => buildQmsQuestionFormTourSteps({ multiDraft: formDrafts.length > 1 }),
    [formDrafts.length],
  );

  const resetFormModal = () => {
    setEditingRow(null);
    setFormDrafts([createEmptyQuestionDraft()]);
    setFormErrors({});
    setIsReadOnly(false);
  };

  const resetImportForm = () => {
    setImportDrafts([]);
    setImportErrors([]);
    setImportRowErrors({});
    setImportFileName("");
    if (importInputRef.current) importInputRef.current.value = "";
  };

  const closeModal = () => {
    setModalMode(null);
    resetFormModal();
    resetImportForm();
  };

  const denyQuestionAction = (actionLabel) => {
    showAlert({
      title: "Permission denied",
      message: `You do not have permission to ${actionLabel}. Ask your administrator to grant the required Question Master action permissions.`,
      type: "warning",
    });
  };

  const openAddModal = () => {
    if (!canQmsQuestionAddEdit) {
      denyQuestionAction("add questions");
      return;
    }
    resetFormModal();
    setModalMode(MODAL_FORM);
  };

  const openImportModal = () => {
    if (!canQmsQuestionImportBulk) {
      denyQuestionAction("import questions");
      return;
    }
    resetImportForm();
    setModalMode(MODAL_IMPORT);
  };

  const openEditModal = async (row, { readOnly = false } = {}) => {
    if (!canQmsQuestionAddEdit) {
      denyQuestionAction("edit questions");
      return;
    }
    if (!userData?.OrganizationId) return;
    try {
      showLoader("Loading question...");
      const response = await fetchQuestionV2(userData, row.QuestionId);
      const questionRows = getApiTable(response, "Question");
      const optionRows = getApiTable(response, "QuestionOptions");
      const q = questionRows[0] || row;

      setEditingRow(q);
      setFormDrafts([
        {
          ...createEmptyQuestionDraft(`edit-${q.QuestionId}`),
          questionId: q.QuestionId,
          questionText: q.QuestionText || "",
          options: optionRows.length
            ? optionRows.map((o, idx) => ({
                SerialNumber: o.SerialNumber || idx + 1,
                OptionText: o.OptionText || "",
                IsCorrect: o.IsCorrect === true || o.IsCorrect === 1,
              }))
            : DEFAULT_QUESTION_OPTIONS.map((o) => ({ ...o })),
        },
      ]);
      setFormErrors({});
      setIsReadOnly(readOnly);
      setModalMode(MODAL_FORM);
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to load question.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleEditClick = (row) => {
    if (!canQmsQuestionAddEdit) {
      denyQuestionAction("edit questions");
      return;
    }
    openEditModal(row, { readOnly: Number(row.QuizUsageCount) > 0 });
  };

  const validateFormDrafts = () => {
    const next = {};
    formDrafts.forEach((draft) => {
      const draftErrors = validateQuestionDraft(draft);
      if (Object.keys(draftErrors).length) next[draft.id] = draftErrors;
    });
    setFormErrors(next);
    return Object.keys(next).length === 0;
  };

  const loadQuestions = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    try {
      setListLoading(true);
      const response = await fetchQuestionsV2(userData);
      setRows(getApiTable(response, "Questions"));
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to load question master.",
        type: "danger",
      });
      setRows([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.OrganizationId && userData?.StaffId) {
      loadQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId, userData?.StaffId]);

  useEffect(() => {
    if (!userData?.StaffId) return undefined;
    if (hasCompletedQmsQuestionBankListTour(userData.StaffId)) return undefined;
    const timer = window.setTimeout(() => setShowListTour(true), 500);
    return () => window.clearTimeout(timer);
  }, [userData?.StaffId]);

  useEffect(() => {
    if (modalMode !== MODAL_FORM || editingRow) {
      setShowFormTour(false);
      return undefined;
    }
    if (!userData?.StaffId) return undefined;
    if (hasCompletedQmsQuestionFormTour(userData.StaffId)) return undefined;
    const timer = window.setTimeout(() => setShowFormTour(true), 450);
    return () => window.clearTimeout(timer);
  }, [modalMode, editingRow, userData?.StaffId]);

  const refreshAfterSave = async () => {
    await loadQuestions();
    onQuestionsChanged?.();
  };

  const persistDrafts = async (drafts) => {
    if (editingRow) {
      const draft = drafts[0];
      return saveQuestionV2(userData, {
        QuestionId: editingRow.QuestionId,
        QuestionText: draft.questionText.trim(),
        OptionsJson: buildQuestionOptionsXml(
          draft.options.filter((o) => o.OptionText.trim()),
        ),
      });
    }

    if (drafts.length === 1) {
      const draft = drafts[0];
      return saveQuestionV2(userData, {
        QuestionId: "",
        QuestionText: draft.questionText.trim(),
        OptionsJson: buildQuestionOptionsXml(
          draft.options.filter((o) => o.OptionText.trim()),
        ),
      });
    }

    return saveQuestionsBulkV2(userData, buildQuestionsBulkXml(drafts));
  };

  const handleSave = async ({ addAnother = false } = {}) => {
    if (isReadOnly) return;
    if (!canQmsQuestionAddEdit) {
      denyQuestionAction(editingRow ? "edit questions" : "add questions");
      return;
    }
    if (!validateFormDrafts() || !userData?.OrganizationId || !userData?.StaffId) {
      return;
    }

    const isEdit = !!editingRow;
    const count = formDrafts.length;
    const loaderMessage = isEdit
      ? "Updating question..."
      : count === 1
        ? "Saving question..."
        : `Saving ${count} questions...`;

    try {
      showLoader(loaderMessage);
      const response = await persistDrafts(formDrafts);
      const row = firstSuccessStoryRow(response);

      if (isApiSuccess(response, row)) {
        showAlert({
          title: "Success",
          message:
            apiMessage(response, row) ||
            (isEdit
              ? "Question updated."
              : count === 1
                ? "Question saved."
                : `${count} questions saved.`),
          type: "success",
        });
        await refreshAfterSave();

        if (addAnother && !isEdit) {
          resetFormModal();
        } else {
          closeModal();
        }
      } else {
        showAlert({
          title: "Error",
          message: apiMessage(response, row) || "Could not save question(s).",
          type: "danger",
        });
      }
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to save question(s).",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportFileName(file.name);
    const { drafts, errors: parseErrors } = await parseQuestionImportFile(file);
    setImportDrafts(drafts);
    setImportErrors(parseErrors);
    setImportRowErrors(validateImportDrafts(drafts));
  };

  const updateImportDraft = (id, updater) => {
    setImportDrafts((prev) => {
      const next = prev.map((d) => (d.id === id ? updater(d) : d));
      setImportRowErrors(validateImportDrafts(next));
      return next;
    });
  };

  const removeImportDraft = (id) => {
    setImportDrafts((prev) => {
      const next = prev.filter((d) => d.id !== id);
      setImportRowErrors(validateImportDrafts(next));
      return next;
    });
  };

  const handleImportCorrectChange = (id, value) => {
    updateImportDraft(id, (d) => setCorrectOptionNumber(d, value));
  };

  const handleImportSave = async () => {
    if (!canQmsQuestionImportBulk) {
      denyQuestionAction("import questions");
      return;
    }
    if (!importDrafts.length) {
      showAlert({
        title: "Nothing to import",
        message: importErrors[0] || "Upload a valid template file first.",
        type: "warning",
      });
      return;
    }

    if (importErrors.length || Object.keys(importRowErrors).length) {
      showAlert({
        title: "Fix import errors",
        message:
          importErrors[0] ||
          Object.values(importRowErrors)[0] ||
          "Fix highlighted rows before import.",
        type: "warning",
      });
      return;
    }

    if (!userData?.OrganizationId || !userData?.StaffId) return;

    try {
      showLoader(`Importing ${importDrafts.length} question(s)...`);
      const response = await saveQuestionsBulkV2(
        userData,
        buildQuestionsBulkXml(importDrafts),
      );
      const row = firstSuccessStoryRow(response);

      if (isApiSuccess(response, row)) {
        showAlert({
          title: "Import complete",
          message:
            apiMessage(response, row) ||
            `${importDrafts.length} question(s) imported successfully.`,
          type: "success",
        });
        closeModal();
        await refreshAfterSave();
      } else {
        showAlert({
          title: "Import failed",
          message: apiMessage(response, row) || "Could not import questions.",
          type: "danger",
        });
      }
    } catch (error) {
      showAlert({
        title: "Import failed",
        message: error?.message || "Failed to import questions.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleDeleteClick = (row) => {
    if (!canQmsQuestionDelete) {
      denyQuestionAction("delete questions");
      return;
    }
    if (Number(row.QuizUsageCount) > 0) {
      showAlert({
        title: "Cannot delete",
        message: "This question is mapped to a quiz and cannot be deleted.",
        type: "warning",
      });
      return;
    }
    setConfirmModal({ open: true, row });
  };

  const handleDeleteConfirm = async () => {
    const row = confirmModal.row;
    if (!row || !userData?.OrganizationId) {
      setConfirmModal({ open: false, row: null });
      return;
    }
    if (!canQmsQuestionDelete) {
      denyQuestionAction("delete questions");
      setConfirmModal({ open: false, row: null });
      return;
    }

    try {
      showLoader("Deleting question...");
      const response = await deleteQuestionV2(userData, row.QuestionId);
      const resRow = firstSuccessStoryRow(response);
      if (isApiSuccess(response, resRow)) {
        showAlert({
          title: "Deleted",
          message: apiMessage(response, resRow) || "Question deleted.",
          type: "success",
        });
        await refreshAfterSave();
      } else {
        showAlert({
          title: "Error",
          message: apiMessage(response, resRow) || "Could not delete question.",
          type: "danger",
        });
      }
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to delete question.",
        type: "danger",
      });
    } finally {
      hideLoader();
      setConfirmModal({ open: false, row: null });
    }
  };

  const updateDraft = (draftId, updater) => {
    setFormDrafts((prev) =>
      prev.map((draft) => (draft.id === draftId ? updater(draft) : draft)),
    );
  };

  const addDraft = () => {
    setFormDrafts((prev) => [...prev, createEmptyQuestionDraft()]);
  };

  const removeDraft = (draftId) => {
    setFormDrafts((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((draft) => draft.id !== draftId);
    });
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next[draftId];
      return next;
    });
  };

  const tableRows = useMemo(
    () =>
      (rows || []).filter((row) => matchesQmsDateRange(row?.CreatedAt, createdDateRange)),
    [createdDateRange, rows],
  );

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === "QuestionText") {
        const text = String(row.QuestionText || "");
        const preview = text.length > 120 ? `${text.slice(0, 120)}…` : text;
        if (!canQmsQuestionAddEdit) {
          return (
            <span className="qms-question-preview" title={text || undefined}>
              {preview || "—"}
            </span>
          );
        }
        return (
          <button
            type="button"
            className="report-table__cell-link qms-quiz-hub__quiz-name-link"
            onClick={() => handleEditClick(row)}
            title={text || undefined}
          >
            {preview || "—"}
          </button>
        );
      }
      if (field === "CreatedAt") {
        return utils.formatDateTimeDisplay(row.CreatedAt) || "—";
      }
      if (field === "OptionCount" || field === "QuizUsageCount") {
        const value = row?.[field];
        return value == null || value === "" ? "—" : String(value);
      }
      return String(row?.[field] ?? "—");
    },
    // handleEditClick is stable enough for this panel lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canQmsQuestionAddEdit],
  );

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    if (field === "CreatedAt") return true;
    const raw = String(searchValue || "").trim();
    if (!raw) return true;
    return String(row?.[field] ?? "")
      .toLowerCase()
      .includes(raw.toLowerCase());
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "CreatedAt") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={createdDateRange}
              onChange={setCreatedDateRange}
              placeholder="Created range"
              presetOptions={QMS_COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }
      return null;
    },
    [createdDateRange],
  );

  const tableActions = useMemo(
    () =>
      canQmsQuestionDelete
        ? [
            {
              label: "Delete",
              icon: "fas fa-trash-alt",
              onClick: handleDeleteClick,
            },
          ]
        : null,
    // handleDeleteClick is stable enough for this panel lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canQmsQuestionDelete],
  );

  const questionDefaultColumns = useMemo(
    () =>
      canQmsQuestionDelete
        ? [...DEFAULT_QUESTION_COLUMNS, "rowActions"]
        : DEFAULT_QUESTION_COLUMNS,
    [canQmsQuestionDelete],
  );

  const layoutStorageKey = useMemo(
    () =>
      userData?.StaffId
        ? `qms-quiz-manager-questions-layout-v5-${userData.StaffId}`
        : "",
    [userData?.StaffId],
  );

  const isEditMode = !!editingRow;
  const draftCount = formDrafts.length;
  const showModal = !!modalMode;
  const importHasRowErrors = Object.keys(importRowErrors).length > 0;

  const modalTitle =
    modalMode === MODAL_IMPORT
      ? "Import questions"
      : isEditMode
        ? isReadOnly
          ? "View question"
          : "Edit question"
        : draftCount > 1
          ? `Add questions (${draftCount})`
          : "Add question";

  const headerActions = (
    <div className="qms-quiz-hub__header-actions" data-tour="qms-question-list-toolbar">
      <button
        type="button"
        className="qms-tour-help-btn"
        data-tour="qms-question-list-guide"
        title="Show question list guide"
        onClick={() => setShowListTour(true)}
      >
        <i className="fas fa-circle-question me-1" aria-hidden="true" />
        Guide
      </button>
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={loadQuestions}
      >
        <i className="fas fa-refresh me-1" aria-hidden="true" />
        Refresh
      </button>
      {canQmsQuestionAddEdit ? (
        <button type="button" className="btn btn-primary btn-sm" onClick={openAddModal}>
          <i className="fas fa-plus me-1" aria-hidden="true" />
          Add question
        </button>
      ) : null}
      {canQmsQuestionImportBulk ? (
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={openImportModal}
        >
          <i className="fas fa-file-import me-1" aria-hidden="true" />
          Import Excel
        </button>
      ) : null}
    </div>
  );

  return (
    <>
      {actionsSlot ? createPortal(headerActions, actionsSlot) : null}

      <div className="qms-doc-lib__report-wrap" data-tour="qms-question-list-table">
        <ReportTable
          rows={tableRows}
          fieldOptions={QUESTION_FIELD_OPTIONS}
          defaultColumns={questionDefaultColumns}
          renderCell={renderReportCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          pageSizeOptions={[10, 25, 50, 100]}
          columnSearchMode="client"
          rowActions={tableActions}
          persistLayoutKey={layoutStorageKey}
          loading={listLoading}
        />
      </div>

      <IsfPageTour
        open={showListTour}
        steps={questionListTourSteps}
        staffId={userData?.StaffId || ""}
        markTourCompleted={markQmsQuestionBankListTourCompleted}
        onClose={() => setShowListTour(false)}
      />

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, row: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete question"
        message="Are you sure you want to delete this question from the bank?"
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass="btn-danger"
      />

      {showModal && (
        <QmsModalPortal>
          <div
            className={`modal fade show d-block qms-quiz-hub-modal${
              modalMode === MODAL_IMPORT ? " qms-question-import-modal" : ""
            }`}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div
              className={`modal-dialog${
                modalMode === MODAL_IMPORT
                  ? " qms-question-import-modal__dialog"
                  : " modal-xl modal-dialog-scrollable"
              }`}
            >
              <div
                className={`modal-content${
                  modalMode === MODAL_IMPORT ? " qms-question-import-modal__content" : ""
                }`}
              >
                <div className="modal-header py-2">
                  <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                    <h5 className="modal-title mb-0">{modalTitle}</h5>
                    {modalMode === MODAL_FORM && !isEditMode && (
                      <button
                        type="button"
                        className="qms-tour-help-btn ms-auto"
                        data-tour="qms-question-form-guide"
                        title="Show add question guide"
                        onClick={() => setShowFormTour(true)}
                      >
                        <i className="fas fa-circle-question" aria-hidden="true" />
                        Guide
                      </button>
                    )}
                  </div>
                  <button type="button" className="btn-close" onClick={closeModal} />
                </div>

                <div
                  className={`modal-body${
                    modalMode === MODAL_IMPORT
                      ? " qms-question-import-modal__body"
                      : " qms-question-form-modal"
                  }`}
                >
                  {modalMode === MODAL_FORM && isReadOnly ? (
                    <div className="qms-quiz-hub__inline-alert" role="status">
                      <i className="fas fa-info-circle" aria-hidden="true" />
                      <span>
                        This question is mapped to a quiz and cannot be edited. Create a
                        new question for changes.
                      </span>
                    </div>
                  ) : null}
                  {modalMode === MODAL_FORM && (
                    <QmsQuestionEntrySheet
                      drafts={formDrafts}
                      errorsById={formErrors}
                      isEditMode={isEditMode}
                      readOnly={isReadOnly}
                      onAddDraft={addDraft}
                      onRemoveDraft={removeDraft}
                      onUpdateDraft={updateDraft}
                    />
                  )}

                  {modalMode === MODAL_IMPORT && (
                    <QmsQuestionImportSheet
                      drafts={importDrafts}
                      errorsById={importRowErrors}
                      fileName={importFileName}
                      parseErrors={importErrors}
                      importInputRef={importInputRef}
                      onDownloadTemplate={downloadQuestionImportTemplate}
                      onFileChange={handleImportFileChange}
                      onUpdateDraft={updateImportDraft}
                      onRemoveDraft={removeImportDraft}
                      onCorrectOptionChange={handleImportCorrectChange}
                    />
                  )}
                </div>

                <div className="modal-footer py-2" data-tour="qms-question-form-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={closeModal}>
                    {isReadOnly ? "Close" : "Cancel"}
                  </button>

                  {modalMode === MODAL_FORM && !isReadOnly && !isEditMode && draftCount === 1 && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => handleSave({ addAnother: true })}
                    >
                      Save &amp; add another
                    </button>
                  )}

                  {modalMode === MODAL_FORM && !isReadOnly && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave()}
                    >
                      {isEditMode
                        ? "Save"
                        : draftCount > 1
                          ? `Save all (${draftCount})`
                          : "Save"}
                    </button>
                  )}

                  {modalMode === MODAL_IMPORT && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleImportSave}
                      disabled={
                        !importDrafts.length || importErrors.length > 0 || importHasRowErrors
                      }
                    >
                      Import {importDrafts.length ? `(${importDrafts.length})` : ""}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />

          {modalMode === MODAL_FORM && !isEditMode && (
            <IsfPageTour
              open={showFormTour}
              steps={questionFormTourSteps}
              staffId={userData?.StaffId || ""}
              markTourCompleted={markQmsQuestionFormTourCompleted}
              onClose={() => setShowFormTour(false)}
            />
          )}
        </QmsModalPortal>
      )}
    </>
  );
};

export default QmsQuestionBankPanel;
